import React from 'react';
import type { Order } from '@Types/cart/Order';
import ChevronRightIcon from 'components/icons/chevron-right';
import Image from 'frontastic/lib/image';
import { useDateFormat, useFormat } from 'helpers/hooks/useFormat';
import imageUrlResize from 'helpers/imageUrlResize';
import { OrderUtils } from '../../../../utils/OrderUtils';

type OrderItemProps = {
  order: Order;
  link: string;
  onClick?: (data?: any) => void;
};

const OrderItem: React.FC<OrderItemProps> = ({ order, link, onClick }) => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { formatDate } = useDateFormat();

  const itemCount = order.lineItems?.length ?? 0;
  const displayItem = order.lineItems?.reduce(
    (currentItem, nextItem) =>
      !currentItem?.price?.centAmount || currentItem.price.centAmount < nextItem.price?.centAmount
        ? nextItem
        : currentItem,
    undefined,
  );
  const orderState = OrderUtils.getOrderState(order);

  if (!displayItem) {
    return null;
  }

  const onLinkClick = (e) => {
    e.preventDefault();
    onClick(order);
    window.location.hash = `${link}-${order.orderId}`;
  };

  return (
    <a href="#" onClick={(e) => onLinkClick(e)} className="cursor-pointer">
      <div className="flex max-w-[674px] cursor-pointer flex-row items-center justify-between border border-neutral-200 bg-white transition duration-200 ease-in-out hover:bg-neutral-50 lg:p-4">
        <div className="flex flex-col justify-center gap-2 px-4">
          <div className="w-20 bg-neutral-200 mix-blend-multiply md:w-60">
            {displayItem.variant?.images?.[0] && (
              <Image src={imageUrlResize(displayItem.variant.images[0], 'medium')} alt={displayItem.name} />
            )}
          </div>
          <div className="flex w-fit items-center justify-center self-center rounded-full bg-primary-200 px-2 text-[11px] md:hidden">
            {formatAccountMessage({
              id: `order.status.${orderState}`,
              defaultMessage: orderState,
            })}
          </div>
        </div>
        <div className="flex flex-col gap-2 py-4">
          <div className="font-bold lg:text-md">
            {formatAccountMessage({
              id: 'order.from',
              defaultMessage: 'order from {orderDate}',
              values: { orderDate: formatDate(order.createdAt) },
            })}
          </div>
          <div className="text-xs">{displayItem.name}</div>
          {itemCount > 1 && (
            <div className="text-xs">
              {formatAccountMessage({
                id: `order.items.additional_${itemCount === 2 ? 'one' : 'other'}`,
                defaultMessage: `+ {count} ${itemCount === 2 ? 'product' : 'products'}`,
                values: { count: itemCount - 1 },
              })}
            </div>
          )}
          <div className="text-xs">
            {formatAccountMessage({ id: 'orders.number', defaultMessage: 'Order number:' })}:{' '}
            <strong>{order.orderNumber ?? order.orderId}</strong>
          </div>
          <div className="hidden w-fit items-center justify-center rounded-full bg-primary-200 px-2 text-[11px] md:block">
            {formatAccountMessage({
              id: `order.status.${orderState}`,
              defaultMessage: orderState,
            })}
          </div>
        </div>
        <div className="flex items-center px-4">
          <ChevronRightIcon className="size-6" />
        </div>
      </div>
    </a>
  );
};

export default OrderItem;
